import { Controller } from "stimulus";
import consumer from "channels/consumer";
import { formatMoney } from "utils/money";
import { applyAvatar } from "utils/avatar";

// Matches only http(s):// URLs (bare domains and javascript:/data:/mailto: never match). XSS-safety
// comes from the DOM build path (createElement + setAttribute + textContent, never innerHTML) and the
// forced https?:// scheme; the "<" exclusion is defense-in-depth. Flags are applied per use in fillBody.
const CHAT_URL_REGEX = /https?:\/\/[^\s<]+/;

// The counter only appears once a message is this close (or closer) to the input's maxlength,
// so it stays out of the way for the vast majority of short messages.
const COUNTER_VISIBLE_REMAINING = 50;

// A fast double-click on the trigger icon lands its second click on the backdrop the dialog just
// opened over (same screen position), so a backdrop dismiss within this window of opening is ignored.
const CONFIRM_DISMISS_GRACE_MS = 250;

export default class ChatPanel extends Controller {
  static targets = [
    "messages", "input", "submit", "banned", "closed", "hint", "counter", "deleteIcon", "banIcon", "moderatorBadge",
    "announcement", "announcementBody", "announcementText", "announcementToggle", "announcementPlaceholder",
    "messageTemplate", "tipMessageTemplate", "moderatorActionsTemplate", "tipModeratorActionsTemplate",
    "confirm", "confirmCancel", "confirmAccept", "confirmPreview", "confirmPreviewAvatar",
    "confirmPreviewAvatarFallback", "confirmPreviewName", "confirmPreviewAmount", "confirmPreviewBody",
  ];
  static values = {
    currentUserId: Number,
    moderator: Boolean,
    contextType: String,
    contextId: Number,
    heartbeatIntervalMs: Number,
    subscriberProfileBaseUrl: String,
    // Set only by the live console, where the banner is editable in place. An editable banner has
    // to stay on screen when empty, or there is nothing left for the creator to click.
    announcementEditable: Boolean,
  };

  connect() {
    this.banned = false;
    this.closed = false;
    this.announcementExpanded = false;
    // Ids of messages we sent, so their echo back over the cable is recognized as our own (see
    // received()'s "message" branch) rather than treated like any other viewer's message.
    this.sentClientMsgIds = new Set();
    // Tracks whether the reader was at the bottom of the list, kept current via the scroll
    // listener below. Initialized eagerly (not left undefined) so the very first keyboard-open
    // resize on mobile already has a valid value to re-pin against.
    this.pinnedToBottom = true;

    // Stimulus singular target getters are not memoized (each access re-queries the
    // controller's subtree, which only grows as messages accumulate for the session), so
    // resolve these once and reuse the cached elements from the per-message build methods.
    this.messageTemplateEl = this.messageTemplateTarget;
    this.tipMessageTemplateEl = this.tipMessageTemplateTarget;
    if (this.hasModeratorActionsTemplateTarget) this.moderatorActionsTemplateEl = this.moderatorActionsTemplateTarget;
    if (this.hasTipModeratorActionsTemplateTarget) this.tipModeratorActionsTemplateEl = this.tipModeratorActionsTemplateTarget;

    this.pendingConfirm = null;
    this.confirmTrigger = null;
    this.confirmOpenedAt = 0;
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden");

    this.onConfirmKeydown = (event) => {
      if (!this.pendingConfirm) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.cancelConfirm();
        return;
      }
      if (event.key !== "Tab") return;

      event.preventDefault();
      const stops = [this.confirmCancelTarget, this.confirmAcceptTarget];
      const next = stops[(stops.indexOf(document.activeElement) + (event.shiftKey ? -1 : 1) + 2) % 2];
      next.focus();
    };
    document.addEventListener("keydown", this.onConfirmKeydown);

    this.subscription = consumer.subscriptions.create(
      {
        channel: "LiveStreamChannel",
        context_type: this.contextTypeValue,
        context_id: this.contextIdValue,
      },
      {
        received: this.received.bind(this),
        connected: this.onConnected.bind(this),
        disconnected: this.onDisconnected.bind(this),
      }
    );

    // The announcement chevron only appears when the 2-line-clamped text overflows;
    // re-check on resize because the chat panel width changes across breakpoints.
    this.onResize = () => this.scheduleAnnouncementToggleRefresh();
    window.addEventListener("resize", this.onResize);
    this.scheduleAnnouncementToggleRefresh();

    // Keep pinnedToBottom current as the reader scrolls, so a later resize (e.g. the mobile
    // keyboard opening/closing) knows whether it should re-pin.
    this.onMessagesScroll = () => { this.pinnedToBottom = this.isPinnedToBottom(); };
    this.messagesTarget.addEventListener("scroll", this.onMessagesScroll, { passive: true });

    // The visual viewport (not window) resizes when the on-screen keyboard opens/closes on
    // mobile; re-pin to the bottom across that resize so a pinned reader never loses their place.
    this.onViewportResize = () => {
      if (this.pinnedToBottom) {
        window.requestAnimationFrame(() => {
          if (this.hasMessagesTarget) this.scrollToBottom();
        });
      }
    };
    if (window.visualViewport) window.visualViewport.addEventListener("resize", this.onViewportResize);

    // Keep the server-side presence entry fresh. The interval comes from
    // LiveViewerPresence::HEARTBEAT_INTERVAL and must stay well under the server's
    // PRESENCE_TTL (45s) so a tab is never evicted while open. The immediate ping
    // fires from onConnected (once the channel is actually up).
    this.heartbeat = window.setInterval(() => this.subscription.perform("heartbeat"), this.heartbeatIntervalMsValue);

    // Re-register promptly after the browser throttles background timers.
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.subscription.perform("heartbeat");
    };
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    // A Turbo-restored page can have a pre-filled input, so render a correct counter immediately.
    this.updateCounter();
  }

  disconnect() {
    this.stopRealtime();
    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    if (this.onResize) window.removeEventListener("resize", this.onResize);
    if (this.onVisibilityChange) document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.onMessagesScroll && this.hasMessagesTarget) this.messagesTarget.removeEventListener("scroll", this.onMessagesScroll);
    if (window.visualViewport && this.onViewportResize) window.visualViewport.removeEventListener("resize", this.onViewportResize);
    if (this.onConfirmKeydown) document.removeEventListener("keydown", this.onConfirmKeydown);
  }

  // Shared by disconnect() and handleRoomClosed(): drops the cable subscription and stops the
  // presence heartbeat.
  stopRealtime() {
    if (this.subscription) {
      consumer.subscriptions.remove(this.subscription);
      this.subscription = null;
    }
    if (this.heartbeat) window.clearInterval(this.heartbeat);
  }

  // The room has closed (live-pages--show#closeRoom, either kind) - wind the live parts of the
  // panel down in place, same as a ban, but independent of it.
  handleRoomClosed() {
    this.stopRealtime();
    this.setInputDisabled(true);
    this.closed = true;
    if (this.hasClosedTarget) this.closedTarget.classList.remove("hidden");
  }

  onConnected() {
    this.enableInput();
    // Register presence immediately on (re)connect, before the first interval tick.
    this.subscription.perform("heartbeat");
  }

  onDisconnected() {
    this.setInputDisabled(true);
  }

  received(data) {
    switch (data.type) {
      case "backfill":
        this.messagesTarget.replaceChildren();
        (data.messages || []).forEach((message) => this.appendMessage(message));
        this.scrollToBottom();
        break;
      case "message": {
        // Our own message echoed back always scrolls into view, even if the reader had scrolled
        // up before sending. `pinned` must be computed here, before appendMessage() below changes
        // scrollHeight - isPinnedToBottom() after the append would no longer reflect pre-send state.
        const mine = this.sentClientMsgIds.delete(data.client_msg_id);
        const pinned = mine || this.isPinnedToBottom();
        this.appendMessage(data);
        if (pinned) this.scrollToBottom();
        break;
      }
      case "remove":
        this.removeMessage(data.client_msg_id);
        break;
      case "ban":
        this.handleBan(data.user_id);
        break;
      case "viewers":
        this.updateViewerCount(data.count);
        break;
      case "rejected":
        if (data.reason === "slow_mode") this.handleSlowMode();
        break;
      case "announcement":
        this.setAnnouncement(data.body, data.body_html);
        break;
      case "poll":
        // Forward poll events to the floating card (window event so the card may be absent).
        this.dispatch("poll", { detail: data, target: window });
        break;
      case "tip":
        // A tip renders in BOTH places (deduped by id): highlighted inline AND in the pinned
        // carousel. The carousel may be absent (dashboard / flag off), so forward via a window event.
        if (data.action === "created") {
          this.addInlineTip(data.tip);
          this.dispatch("tip", { detail: data.tip, target: window });
        } else if (data.action === "removed") {
          const id = data.tip && data.tip.id;
          this.removeInlineTip(id);
          this.dispatch("tip-removed", { detail: { id }, target: window });
        }
        break;
      case "tips_backfill":
        // Subscribe-time backfill (latest-first) for the carousel only; the inline scroll is
        // rebuilt from the chat backfill and does not replay past tips.
        this.dispatch("tips-backfill", { detail: data.tips || [], target: window });
        break;
    }
  }

  // Count renders twice (mobile span + desktop pill, both outside this controller's element) -
  // a single querySelector only ever hit the first (mobile) node, freezing the other viewport's.
  updateViewerCount(count) {
    document.querySelectorAll("[data-live-viewer-count]").forEach((el) => {
      el.setAttribute("data-rolling-number-count-value", count);
    });
  }

  // Cast a vote for the floating card over our shared subscription (it has none of its own).
  castVote(event) {
    if (!this.subscription) return;

    this.subscription.perform("vote", {
      poll_id: event.detail.pollId,
      option_id: event.detail.optionId,
    });
  }

  setAnnouncement(body, bodyHtml) {
    if (!this.hasAnnouncementTarget) return;

    const text = body || "";
    // body_html is server-escaped + linkified, so it's safe to inject; fall back to plain text if an older payload omits it.
    if (bodyHtml != null) {
      this.announcementBodyTarget.innerHTML = bodyHtml;
    } else {
      this.announcementBodyTarget.textContent = text;
    }
    // This is the single authority on what the banner reads, including for the creator who just
    // typed it: LiveEventsController#update broadcasts on save, so the inline editor never writes
    // the text itself and the linkified markup can't be raced away by a plain-text local write.
    const blank = text.trim() === "";
    this.announcementBodyTarget.hidden = blank;
    if (this.hasAnnouncementPlaceholderTarget) this.announcementPlaceholderTarget.hidden = !blank;

    this.announcementTarget.classList.toggle("hidden", blank && !this.announcementEditableValue);
    this.collapseAnnouncement();
    this.scheduleAnnouncementToggleRefresh();
  }

  toggleAnnouncement() {
    this.announcementExpanded = !this.announcementExpanded;
    this.announcementTextTarget.classList.toggle("line-clamp-2", !this.announcementExpanded);
    this.announcementToggleTarget.classList.toggle("rotate-180", this.announcementExpanded);
    this.announcementToggleTarget.setAttribute("aria-expanded", String(this.announcementExpanded));
  }

  collapseAnnouncement() {
    this.announcementExpanded = false;
    if (this.hasAnnouncementTextTarget) this.announcementTextTarget.classList.add("line-clamp-2");
    if (this.hasAnnouncementToggleTarget) {
      this.announcementToggleTarget.classList.remove("rotate-180");
      this.announcementToggleTarget.setAttribute("aria-expanded", "false");
    }
  }

  scheduleAnnouncementToggleRefresh() {
    window.requestAnimationFrame(() => this.refreshAnnouncementToggle());
  }

  // Show the expand chevron only when the clamped text overflows two lines. While
  // expanded the chevron stays (it's the collapse control), so only measure when collapsed.
  refreshAnnouncementToggle() {
    if (!this.hasAnnouncementToggleTarget || !this.hasAnnouncementTextTarget) return;
    if (this.announcementExpanded) return;

    const el = this.announcementTextTarget;
    const overflowing = el.scrollHeight > el.clientHeight + 1;
    this.announcementToggleTarget.classList.toggle("hidden", !overflowing);
  }

  send(event) {
    event.preventDefault();

    const body = this.inputTarget.value.trim();
    if (body === "") return;

    // Generated once: the same id is tracked in sentClientMsgIds (so received() can recognize
    // our own echo) and sent as client_msg_id, so they must never drift apart.
    const clientMsgId = this.newClientMsgId();
    this.sentClientMsgIds.add(clientMsgId);
    this.subscription.perform("speak", { body, client_msg_id: clientMsgId });
    this.clearInput();
    this.focusInput();
  }

  // Keeps the on-screen keyboard open on mobile across a send, since blurring the input closes it.
  focusInput() {
    if (this.hasInputTarget && !this.inputTarget.disabled) this.inputTarget.focus();
  }

  // Clears the input programmatically (a plain value reset fires no `input` event, so the
  // counter is updated explicitly here). Called both from send() above and, cross-controller,
  // from chat-tip's onSucceeded() after a successful tip.
  clearInput() {
    this.inputTarget.value = "";
    this.updateCounter();
  }

  // Shows "<length>/<max>" only once the message is within COUNTER_VISIBLE_REMAINING characters
  // of the input's maxlength (read from the DOM so the limit is never declared twice on the
  // client). max <= 0 means no maxlength attribute is set (browsers report -1), which would
  // otherwise satisfy the threshold immediately.
  updateCounter() {
    if (!this.hasInputTarget || !this.hasCounterTarget) return;

    const max = this.inputTarget.maxLength;
    if (max <= 0) return;

    const length = this.inputTarget.value.length;
    const remaining = max - length;
    this.counterTarget.classList.toggle("hidden", remaining > COUNTER_VISIBLE_REMAINING);
    this.counterTarget.textContent = `${length}/${max}`;
  }

  deleteMessage(event) {
    if (!this.moderatorValue) return;

    const clientMsgId = event.params.clientMsgId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "delete",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => this.subscription.perform("delete_message", { client_msg_id: clientMsgId }),
    });
  }

  banUser(event) {
    if (!this.moderatorValue) return;

    const userId = event.params.userId;
    const row = this.rowFor(event);
    this.openConfirm({
      variant: "ban",
      preview: row ? this.previewFromRow(row) : null,
      trigger: event.currentTarget,
      run: () => this.subscription.perform("ban_user", { user_id: userId }),
    });
  }

  // Moderator remove from the inline tip's toolbar (params-based, like deleteMessage).
  removeTip(event) {
    if (!this.moderatorValue) return;

    const row = this.rowFor(event);
    this.confirmRemoveTip(event.params.tipId, { trigger: event.currentTarget, preview: row ? this.previewFromRow(row) : null });
  }

  // Moderator remove from the pinned carousel. The carousel owns no subscription, so it asks us (which does) via a window event.
  removeCarouselTip(event) {
    if (!this.moderatorValue) return;

    const detail = event.detail || {};
    this.confirmRemoveTip(detail.id, { trigger: detail.trigger || null, preview: detail.tip ? this.previewFromTip(detail.tip) : null });
  }

  // Shared by both tip removal paths: the server unpins + removes it for everyone; no refund.
  confirmRemoveTip(tipId, { trigger, preview } = {}) {
    if (tipId == null) return;

    this.openConfirm({
      variant: "tip",
      preview,
      trigger,
      run: () => this.subscription.perform("remove_tip", { id: tipId }),
    });
  }

  openConfirm({ variant, preview, trigger, run }) {
    if (!this.hasConfirmTarget) {
      run();
      return;
    }
    if (this.pendingConfirm) return;

    this.pendingConfirm = { run };
    this.confirmTrigger = trigger || null;
    this.confirmOpenedAt = Date.now();
    this.showConfirmVariant(variant);
    this.fillConfirmPreview(preview);
    this.confirmTarget.classList.remove("hidden");
    // Cancel takes focus, not Confirm, so a reflex Enter mid-composer can't fire the destructive action.
    this.confirmCancelTarget.focus();
  }

  acceptConfirm() {
    const pending = this.pendingConfirm;
    this.closeConfirm();

    if (!pending || !this.moderatorValue || !this.subscription) return;

    pending.run();
  }

  cancelConfirm() {
    this.closeConfirm();
  }

  confirmBackdropClick(event) {
    if (event.currentTarget !== event.target) return;
    if (Date.now() - this.confirmOpenedAt < CONFIRM_DISMISS_GRACE_MS) return;

    this.cancelConfirm();
  }

  closeConfirm() {
    const trigger = this.confirmTrigger;

    this.pendingConfirm = null;
    this.confirmTrigger = null;
    if (this.hasConfirmTarget) this.confirmTarget.classList.add("hidden");

    // The Tab-trap and the click-intercepting backdrop mean focus cannot legitimately leave the
    // dialog while it's open, so restoring it here doesn't need to reactively check where focus
    // currently sits (which a backdrop click's own mousedown has already blurred to <body>).
    if (trigger && trigger.isConnected) trigger.focus();
  }

  showConfirmVariant(variant) {
    this.confirmTarget.querySelectorAll("[data-chat-confirm-variant]").forEach((el) => {
      el.hidden = el.dataset.chatConfirmVariant !== variant;
    });
  }

  fillConfirmPreview(preview) {
    const show = !!preview;
    this.confirmPreviewTarget.classList.toggle("hidden", !show);
    this.confirmPreviewTarget.classList.toggle("flex", show);
    if (!show) return;

    applyAvatar(this.confirmPreviewAvatarTarget, this.confirmPreviewAvatarFallbackTarget, preview.user);
    this.confirmPreviewNameTarget.textContent = preview.name || "";
    this.confirmPreviewBodyTarget.textContent = preview.body || "";

    const amount = preview.amount || "";
    this.confirmPreviewAmountTarget.textContent = amount;
    this.confirmPreviewAmountTarget.hidden = amount === "";
  }

  rowFor(event) {
    return event.currentTarget.closest("[data-client-msg-id], [data-tip-id]");
  }

  previewFromRow(row) {
    const nameEl = row.querySelector("[data-chat-message-name]");
    const bodyEl = row.querySelector("[data-chat-message-body]");
    const amountEl = row.querySelector("[data-chat-tip-amount]");
    const avatarEl = row.querySelector("[data-chat-message-avatar]");
    const fallbackEl = row.querySelector("[data-chat-message-avatar-fallback]");

    return {
      name: nameEl ? nameEl.textContent : "",
      body: bodyEl ? bodyEl.textContent : "",
      amount: amountEl ? amountEl.textContent : "",
      user: {
        avatar_url: avatarEl && !avatarEl.classList.contains("hidden") ? avatarEl.src : null,
        initials: fallbackEl ? fallbackEl.textContent : "",
        avatar_color_class: (fallbackEl && fallbackEl.dataset.avatarColorClass) || null,
      },
    };
  }

  previewFromTip(tip) {
    return {
      name: (tip.user && tip.user.name) || "",
      body: (tip.body || "").trim(),
      amount: this.tipAmountLabel(tip),
      user: tip.user || null,
    };
  }

  // Shared by the inline tip row's badge and the confirm preview, so the two can't format differently.
  tipAmountLabel(tip) {
    return `TIP ${formatMoney(tip.amount_cents, tip.currency)}`;
  }

  appendMessage(message) {
    if (this.messagesTarget.querySelector(`[data-client-msg-id="${CSS.escape(message.client_msg_id)}"]`)) return;

    this.messagesTarget.appendChild(this.buildMessage(message));
  }

  // Clones messageTemplate (declared in the panel markup) and fills its slots. Markup lives
  // in the ERB template; this only ever populates it.
  buildMessage(message) {
    const wrapper = this.messageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.clientMsgId = message.client_msg_id;
    wrapper.dataset.userId = message.user.id;

    this.fillAvatar(wrapper, message.user);
    this.fillIdentity(wrapper, message.user.name, !!message.user.is_channel_admin, message.user.id);
    this.fillBody(wrapper.querySelector("[data-chat-message-body]"), message.body, !!message.user.is_moderator);

    if (this.moderatorValue) wrapper.appendChild(this.buildModeratorActions(message));

    return wrapper;
  }

  // Shared by buildMessage and buildTipMessage: shows the real photo when avatar_url is present,
  // otherwise the colored-initials fallback ChatSenderIdentity computed server-side (so it matches
  // AvatarComponent elsewhere in the app).
  fillAvatar(row, user) {
    applyAvatar(
      row.querySelector("[data-chat-message-avatar]"),
      row.querySelector("[data-chat-message-avatar-fallback]"),
      user
    );
  }

  // Shared by buildMessage and buildTipMessage: sets the display name (a new-tab link to the
  // sender's dashboard profile for moderators only) and, for a channel admin, appends the cloned
  // moderator badge into the identity slot already in the row.
  fillIdentity(row, name, isChannelAdmin, userId) {
    const nameEl = row.querySelector("[data-chat-message-name]");
    nameEl.className = `font-semibold ${isChannelAdmin ? "text-accent" : "text-strong-text"}`;

    if (this.moderatorValue && !isChannelAdmin && userId != null) {
      // Channel admins have no subscriber profile (that route 404s), so only link real subscribers.
      // textContent (never innerHTML) keeps the user-controlled display name XSS-safe.
      const link = document.createElement("a");
      link.setAttribute("href", `${this.subscriberProfileBaseUrlValue}/${userId}`);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener");
      link.className = "text-inherit hover:underline";
      link.textContent = name;
      nameEl.replaceChildren(link);
    } else {
      nameEl.textContent = name;
    }

    if (isChannelAdmin) row.querySelector("[data-chat-message-identity]").appendChild(this.buildModeratorBadge());
  }

  // Renders the message body. For moderators, bare https?:// URLs become new-tab links; everyone
  // else's body is plain text (identical to before). Anchors are built via createElement + textContent
  // (never innerHTML), and only https?:// matches, so pasted javascript:/data:/HTML can never become a link.
  fillBody(bodyEl, body, senderIsModerator) {
    const text = body || "";
    if (!senderIsModerator) {
      bodyEl.textContent = text;
      return;
    }
    bodyEl.replaceChildren();
    const regex = new RegExp(CHAT_URL_REGEX.source, "gi"); // fresh instance: global regex is stateful
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      let url = match[0];
      // Don't swallow trailing sentence punctuation, e.g. "see https://x.com." or "(https://x.com)".
      const trailing = url.match(/[.,;:!?)\]}'">]+$/);
      if (trailing) url = url.slice(0, url.length - trailing[0].length);
      const start = match.index;
      const end = start + url.length;
      if (start > lastIndex) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer nofollow");
      link.className = "text-accent underline";
      link.textContent = url;
      bodyEl.appendChild(link);
      lastIndex = end;
      regex.lastIndex = end; // resume after the trimmed URL so trailing punctuation is re-emitted as text
    }
    if (lastIndex < text.length) bodyEl.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  buildModeratorBadge() {
    const badge = document.createElement("span");
    badge.className = "inline-flex items-center";
    badge.setAttribute("role", "img");
    badge.setAttribute("aria-label", "Moderator");
    badge.title = "Moderator";
    badge.appendChild(this.moderatorBadgeTarget.content.cloneNode(true));
    badge.querySelectorAll("svg, img").forEach((el) => el.setAttribute("aria-hidden", "true"));
    return badge;
  }

  // A small icon toolbar that floats over the top-right of the message and only
  // appears on hover, so it never reserves column width in the chat. Hidden +
  // non-interactive until the row is hovered. Icons are cloned from the
  // IconComponent-rendered <template>s in the panel markup; the toolbar shell itself
  // is also cloned from a <template> (moderatorActionsTemplate).
  buildModeratorActions(message) {
    const actions = this.moderatorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-delete-button]");
    remove.appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelClientMsgIdParam = message.client_msg_id;

    const ban = actions.querySelector("[data-chat-message-ban-button]");
    ban.appendChild(this.banIconTarget.content.cloneNode(true));
    ban.dataset.chatPanelUserIdParam = message.user.id;

    return actions;
  }

  removeMessage(clientMsgId) {
    const node = this.messagesTarget.querySelector(`[data-client-msg-id="${CSS.escape(clientMsgId)}"]`);
    if (node) node.remove();
  }

  // Append a tip as a highlighted inline message, deduped by tip id so a redelivered tip
  // (reconnect) never doubles.
  addInlineTip(tip) {
    if (!tip || tip.id == null) return;
    if (this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(tip.id))}"]`)) return;

    const pinned = this.isPinnedToBottom();
    this.messagesTarget.appendChild(this.buildTipMessage(tip));
    if (pinned) this.scrollToBottom();
  }

  // Clones tipMessageTemplate and fills its slots; mirrors buildMessage plus the amount badge.
  buildTipMessage(tip) {
    const wrapper = this.tipMessageTemplateEl.content.cloneNode(true).firstElementChild;
    wrapper.dataset.tipId = tip.id;
    if (tip.user) wrapper.dataset.userId = tip.user.id;

    this.fillAvatar(wrapper, tip.user);
    this.fillIdentity(wrapper, (tip.user && tip.user.name) || "", !!(tip.user && tip.user.is_channel_admin), tip.user && tip.user.id);
    wrapper.querySelector("[data-chat-tip-amount]").textContent = this.tipAmountLabel(tip);

    const bodyEl = wrapper.querySelector("[data-chat-message-body]");
    const body = (tip.body || "").trim();
    if (body !== "") {
      bodyEl.textContent = body;
    } else {
      bodyEl.remove();
    }

    if (this.moderatorValue) wrapper.appendChild(this.buildTipModeratorActions(tip));

    return wrapper;
  }

  // A hover-revealed remove control for the inline tip, mirroring buildModeratorActions with a
  // single action. Reuses the trash icon <template>; the toolbar shell is cloned from
  // tipModeratorActionsTemplate.
  buildTipModeratorActions(tip) {
    const actions = this.tipModeratorActionsTemplateEl.content.cloneNode(true).firstElementChild;

    const remove = actions.querySelector("[data-chat-message-remove-tip-button]");
    remove.appendChild(this.deleteIconTarget.content.cloneNode(true));
    remove.dataset.chatPanelTipIdParam = tip.id;

    return actions;
  }

  removeInlineTip(id) {
    if (id == null) return;
    const node = this.messagesTarget.querySelector(`[data-tip-id="${CSS.escape(String(id))}"]`);
    if (node) node.remove();
  }

  handleBan(userId) {
    this.messagesTarget
      .querySelectorAll(`[data-user-id="${CSS.escape(String(userId))}"]`)
      .forEach((node) => node.remove());

    if (String(userId) === String(this.currentUserIdValue)) {
      this.banned = true;
      this.setInputDisabled(true);
      this.bannedTarget.classList.remove("hidden");
    }
  }

  handleSlowMode() {
    this.enableInput();
    this.hintTarget.textContent = "Slow mode is on — please wait a moment before sending another message.";
    this.hintTarget.classList.remove("hidden");

    if (this.hintTimeout) window.clearTimeout(this.hintTimeout);
    this.hintTimeout = window.setTimeout(() => this.hintTarget.classList.add("hidden"), 4000);
  }

  enableInput() {
    if (this.banned || this.closed) return;
    this.setInputDisabled(false);
  }

  setInputDisabled(disabled) {
    this.inputTarget.disabled = disabled;
    this.submitTarget.disabled = disabled;
  }

  isPinnedToBottom() {
    const el = this.messagesTarget;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }

  newClientMsgId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
